import { z } from 'zod';

import { HtmlEditSchema } from './redline-llm-output';
import { CodeBlockRedlineItemSchema } from './redline-shapes';

/**
 * One reviewable unit of a proposal: a single engine edit paired with the
 * preview cards it generated, under a stable id minted at generation time.
 * The unit per-suggestion review accepts or rejects, materialization applies,
 * and staleness fingerprints.
 */
export const ProposalSuggestionSchema = z.object({
  htmlEdit: HtmlEditSchema,
  id: z.string(),
  previewCards: z.array(CodeBlockRedlineItemSchema),
});

/**
 * A proposal's suggestions: at least one, because a proposal exists only for
 * a failing position and a failing position is one that produced edits.
 */
export const ProposalSuggestionsSchema = z.array(ProposalSuggestionSchema).min(1);

/**
 * The recorded review decisions, keyed by suggestion id. Absence means
 * undecided. `accepted` is immutable (the edit is already the user's own
 * tracked change in the document); `rejected` may be cleared until the rule
 * resolves.
 */
export const ProposalDecisionsSchema = z.record(z.string(), z.enum(['accepted', 'rejected']));

export type ProposalSuggestion = z.infer<typeof ProposalSuggestionSchema>;
export type ProposalDecisions = z.infer<typeof ProposalDecisionsSchema>;
export type SuggestionDecision = ProposalDecisions[string];

/**
 * Whether a suggestion offers a review decision at all. A note suggestion
 * (the AI explaining why no edit was possible) materializes nothing, so
 * accepting it is meaningless — it displays but never counts toward review
 * completion, or a position containing one could never finish.
 */
export function getIsSuggestionReviewable(suggestion: ProposalSuggestion): boolean {
  return suggestion.htmlEdit.suggest._type !== 'note';
}

export function getReviewableSuggestions(suggestions: ProposalSuggestion[]): ProposalSuggestion[] {
  return suggestions.filter(getIsSuggestionReviewable);
}

/**
 * Review progress over a proposal, counting only reviewable suggestions.
 * `total` can be zero for a proposal of nothing but notes — such a proposal
 * is never "fully decided" and review simply doesn't apply to it.
 */
export function getProposalReviewProgress(
  suggestions: ProposalSuggestion[],
  decisions: ProposalDecisions
): { decided: number; total: number } {
  const reviewable = getReviewableSuggestions(suggestions);
  const decided = reviewable.filter(suggestion => decisions[suggestion.id] !== undefined).length;
  return { decided, total: reviewable.length };
}

/** True when every reviewable suggestion has a decision — the auto-resolve gate. */
export function getIsProposalFullyDecided(
  suggestions: ProposalSuggestion[],
  decisions: ProposalDecisions
): boolean {
  const { decided, total } = getProposalReviewProgress(suggestions, decisions);
  return total > 0 && decided === total;
}
